// Single source of truth for the issue lifecycle (API v2, 2026-07-29).
//
// Stored enum (what the DB holds):        open | pending | completed
// Derived display statuses (what UIs      open | pending | overdue |
// show and what `status` filters accept): completed | completed-overdue
//
// 'overdue' is NEVER stored: an issue is overdue when it is 'pending' and its
// resolution_eta has passed; 'completed-overdue' when it completed after its
// ETA. Rows without an ETA are never overdue. The backend sends
// `derived_status` + `is_overdue` alongside `status` on every issue row —
// prefer those over re-deriving client-side.
//
// The Site Representative role and the two-actor resolve/reject flow are GONE.
// The HOD acknowledges an open issue (setting the ETA) and later completes it
// with proof photos + an ATR document.
//
// Anywhere in the FE that needs an issue status label, color, or filter
// option should import from this file. Do not redefine these maps inside
// feature folders.

import type { UserRole } from './roles'

/** Stored lifecycle states. */
export type IssueStatus = 'open' | 'pending' | 'completed'

/** Five-value display statuses — also the values the `status` query param accepts. */
export type IssueDerivedStatus =
  | 'open'
  | 'pending'
  | 'overdue'
  | 'completed'
  | 'completed-overdue'

export const ISSUE_STATUS_LABELS: Record<IssueDerivedStatus, string> = {
  open: 'Open',
  pending: 'Pending',
  overdue: 'Overdue',
  completed: 'Completed',
  'completed-overdue': 'Completed (overdue)',
}

// Pill classes per derived status. Color semantics:
//   amber = open       (raised, waiting for the HOD to acknowledge)
//   blue  = pending    (HOD acknowledged, ETA set, in hand)
//   red   = overdue    (pending past its ETA; demands attention)
//   green = completed  (terminal)
//   teal  = completed-overdue (done, but late — kept distinct for reviews)
export const ISSUE_STATUS_PILL_CLASSES: Record<IssueDerivedStatus, string> = {
  open:                 'bg-amber-50 text-amber-700 border border-amber-200',
  pending:              'bg-blue-50 text-blue-700 border border-blue-200',
  overdue:              'bg-red-50 text-red-700 border border-red-200',
  completed:            'bg-green-50 text-green-700 border border-green-200',
  'completed-overdue':  'bg-teal-50 text-teal-700 border border-teal-200',
}

// Filter dropdown options for any list-of-issues view. The buckets are
// disjoint server-side: 'pending' = acknowledged and NOT overdue.
export const ISSUE_STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: IssueDerivedStatus | ''
  label: string
}> = [
  { value: '', label: 'All Status' },
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
  { value: 'completed-overdue', label: 'Completed (overdue)' },
]

/** True while the issue still needs work (anything not completed). */
export function isOpenIssue(status: IssueStatus | IssueDerivedStatus): boolean {
  return status !== 'completed' && status !== 'completed-overdue'
}

/**
 * Client-side fallback for the display status when the backend's
 * `derived_status` isn't available on a row: overdue only via the explicit
 * `is_overdue` flag (never re-derived from timestamps here — the server owns
 * the clock).
 */
export function deriveIssueStatus(
  status: IssueStatus,
  isOverdue: boolean | undefined,
): IssueDerivedStatus {
  if (status === 'pending' && isOverdue === true) {
    return 'overdue'
  }
  return status
}

/** HOD action gates (lifecycle v2 — the HOD is the only actor). */
export function canAcknowledge(status: IssueStatus, viewerRole: UserRole | undefined): boolean {
  return viewerRole === 'hod' && status === 'open'
}

export function canComplete(status: IssueStatus, viewerRole: UserRole | undefined): boolean {
  return viewerRole === 'hod' && status === 'pending'
}

// Two-level severity (backend migration 2026-07-28): critical = mandatory
// trigger questions, non_critical = everything else. low/medium no longer
// exist — the backend maps old rows to non_critical.
export type IssueSeverity = 'critical' | 'non_critical'

export const ISSUE_SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical:     'Critical',
  non_critical: 'Non-critical',
}

export const ISSUE_SEVERITY_PILL_CLASSES: Record<IssueSeverity, string> = {
  critical:     'bg-red-50 text-red-600 border border-red-200',
  non_critical: 'bg-amber-50 text-amber-700 border border-amber-200',
}

export const ISSUE_SEVERITY_FILTER_OPTIONS: ReadonlyArray<{
  value: IssueSeverity | ''
  label: string
}> = [
  { value: '', label: 'All Severity' },
  { value: 'critical', label: 'Critical' },
  { value: 'non_critical', label: 'Non-critical' },
]

// Convenience helpers — prefer these over inline `ISSUE_STATUS_LABELS[s]`
// so unknown values from the backend don't crash the UI.
export function issueStatusLabel(status: IssueDerivedStatus | string | null | undefined): string {
  if (!status) {return '—'}
  return ISSUE_STATUS_LABELS[status as IssueDerivedStatus] ?? String(status)
}

export function issueStatusPillClass(status: IssueDerivedStatus | string | null | undefined): string {
  if (!status) {return 'bg-gray-50 text-gray-500 border border-gray-200'}
  return (
    ISSUE_STATUS_PILL_CLASSES[status as IssueDerivedStatus] ??
    'bg-gray-50 text-gray-500 border border-gray-200'
  )
}

export function issueSeverityLabel(severity: IssueSeverity | string | null | undefined): string {
  if (!severity) {return '—'}
  return ISSUE_SEVERITY_LABELS[severity as IssueSeverity] ?? String(severity)
}

export function issueSeverityPillClass(severity: IssueSeverity | string | null | undefined): string {
  if (!severity) {return 'bg-gray-50 text-gray-500 border border-gray-200'}
  return (
    ISSUE_SEVERITY_PILL_CLASSES[severity as IssueSeverity] ??
    'bg-gray-50 text-gray-500 border border-gray-200'
  )
}
